import { getFromWindowRef } from '@avantia/client-and-server-utilities';
import { SvgIcon } from '@avantia/style-guide';
import jQuery from 'jquery';
import { BaseSyntheticEvent, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { transferToCustomerPortalAction } from '../actions/transferToCustomerPortalAction';
import { getConfig } from '../config/configuration';
import { triggerLinkFollowedEvent } from '../engineClient/actions/eventActions';
import { getMetaDataOrUndefinedFromAnswers } from '../engineClient/tools/clientAndServerUtilities';
import { isAgent } from '../engineClient/tools/utilities';
import { HpClientAnswerMap } from '../engineClient/types';
import { productId, sectionIds } from '../types/questionConstants';
import { HpClaimsMetaData } from '../types/stateTypes';
import './sideBar.scss';

const { gaTrackingId } = getConfig();

const phoneNumberClass = 'hp-phone-number';
const { makingAClaim, declaration, whatHappened, whatCausedTheIncident, readOnlySummary } = sectionIds;
const sectionsWithFaq = [makingAClaim, declaration, whatHappened, whatCausedTheIncident, readOnlySummary];

export interface HpSideBarProps {
  activeSectionId: string;
  answers: HpClientAnswerMap;
  hpPhoneNumber: string;
  metaData: HpClaimsMetaData;
}

export type HpSideBarComponentFunc = (props: HpSideBarProps) => JSX.Element;

export function SideBar({ activeSectionId, answers, hpPhoneNumber }: HpSideBarProps): JSX.Element {
  useEffect(() => {
    interface WinProps {
      gtag: Gtag.Gtag;
    }
    const { gtag } = getFromWindowRef<WinProps>(['gtag']) || {};
    if (gtag) {
      gtag('js', new Date());
      gtag('config', `${gaTrackingId}/Fz1aCN2lp4gBEJ_Wy_wD`, {
        phone_conversion_number: hpPhoneNumber,
        phone_conversion_css_class: phoneNumberClass
      });
    }
  }, []);

  const dispatch = useDispatch();
  const haveQuestions: ListItem[] = [
    {
      text: 'Contact us',
      className: 'phone-us',
      detail: (
        <>
          <h3 className={phoneNumberClass}>{hpPhoneNumber}</h3> Mon-Fri 9am-8pm &amp; Sat 9am-1pm (Costs the same to
          call as a normal landline, even from mobiles)
        </>
      ),
      hrefKey: 'contactUsLink'
    }
  ];

  const handleExitLinkClick = (event: BaseSyntheticEvent) => {
    const link: Partial<HTMLAnchorElement> = {
      href: 'exitWithoutLoggingIncidentBtn',
      text: 'exitWithoutLogging',
      type: 'sideBarLinks'
    };
    triggerLinkFollowedEvent({ getEvent: () => event, link: link as HTMLAnchorElement });
    dispatch(transferToCustomerPortalAction({ answers }) as unknown as AnyAction);
  };

  const showFaq = sectionsWithFaq.indexOf(activeSectionId) >= 0;

  let faqCard: JSX.Element | null = null;
  if (showFaq) {
    const faqItems = [
      {
        text: 'Does making a claim affect my policy or premium?',
        detail: (
          <>
            When you make a claim, the incident details you provide are shared with other insurers in line with industry
            practice. This could influence the price or availability of insurance offered to you in the future as
            insurers often set their prices based on previous incidents you&apos;ve had.
          </>
        ),
        eventKey: 'claimAffectPremium'
      },
      {
        text: 'How long does it take for a claim to be settled?',
        detail: (
          <>
            As soon as we&apos;ve checked that your claim is covered by your policy, we&apos;ll get the process moving.
            The time it takes to settle your claim will depend on the incident. For example, claims involving a single
            household item, such as a TV, are normally settled very quickly. Claims involving major repair work or a
            property rebuild are likely to take several months.
          </>
        ),
        eventKey: 'timeToSettle'
      },
      {
        text: 'How many claims can I make in a year? ',
        detail: (
          <>
            You can make unlimited claims on your policy. However, this may affect your policy premium and/or the terms,
            such as your excesses, when you renew.
          </>
        ),
        eventKey: 'annualClaimLimit'
      },
      {
        text: 'What do I have to pay if my claim is successful?',
        detail: (
          <>
            The average compulsory excess for a buildings claims is £149 and £136 for contents claims. There are also
            separate compulsory excesses for flooding, subsidence/landslip/heave, and escape of water claims. The total
            excess you will pay is the sum of the relevant compulsory excesses and voluntary excesses (if you have any).
            <br></br> <br></br>You can find out what excesses you&apos;ll need to pay by selecting your policy in the
            &quot;Check my cover&quot; section. Your excesses are also listed in your Policy Documents.
          </>
        ),
        eventKey: 'excessAmount'
      },
      {
        text: 'Will my claim be settled on a new for old basis? ',
        detail: (
          <>
            Yes, your contents insurance will cover the loss or damage of your belongings on a new for old basis,
            meaning that you&apos;ll receive the value of the item to replace it at today’s prices. There may be a
            maximum value we would cover up to depending on the type of item you are claiming for.
          </>
        ),
        eventKey: 'newForOld'
      }
    ];

    faqCard = (
      <div className="av-card av-card-child">
        <h3>Claims FAQs</h3>
        <ul className="av-list-check">{faqItems.map(createListItem)} </ul>
      </div>
    );
  }

  return (
    <div className="sidebar">
      {faqCard}
      <div className="av-card av-card-child">
        <h3>Need help?</h3>
        <ul className="av-list-check">
          {!isAgent(getMetaDataOrUndefinedFromAnswers(productId, answers)) && haveQuestions.map(createListItem)}{' '}
          <li className="exit" onClick={handleExitLinkClick}>
            <span>Exit without logging an incident</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

function createListItem({ hrefKey, text, detail, className, eventKey }: ListItem, index: number): JSX.Element[] {
  const toggle = (event: BaseSyntheticEvent): void => {
    const link: Partial<HTMLAnchorElement> = { href: hrefKey || 'faqClick', text, name: eventKey };
    triggerLinkFollowedEvent({ getEvent: () => event, link: link as HTMLAnchorElement });
    const li = jQuery(event.target).closest('li');
    const div = li.next('div');
    const isVisible = jQuery(div).is(':visible');
    div.css({ display: isVisible ? 'none' : 'block' });

    $('.hp-show', li).toggle(isVisible);
    $('.hp-hide', li).toggle(!isVisible);
  };

  return [
    <li key={'li-' + index} onClick={toggle} className={className}>
      <span>
        <span>{text}</span>
        <SvgIcon className="hp-show" type="angle-right" size="very-small" />
        <SvgIcon className="hp-hide" type="angle-down" size="very-small" style={{ display: 'none' }} />
      </span>
    </li>,
    <div key={'li-detail-' + index} className="av-card">
      <div>{detail}</div>
    </div>
  ];
}

interface ListItem {
  className?: string;
  detail: JSX.Element;
  text: string;
  eventKey?: string;
  hrefKey?: string;
}

// export type HpTransferToCustomerPortalActionFunc = (payload: HpTransferToCustomerPortalPayload) => void;
// interface HpSidebarDispatchProps {
//   transferToCustomerPortal: HpTransferToCustomerPortalActionFunc;
// }

// type HpSidebarMapDispatchToPropsFunc = (dispatch: (payload: any) => any) => HpSidebarDispatchProps;
